import React, { useState, useId, useEffect } from 'react';
import PropTypes from 'prop-types';
import { exportOptions } from '../../utils/exports';
import Button from '../Button/Button';
import { getExport, getSeasonExport, getFile } from '../../utils/api/exports';
import { getSeasons } from '../../utils/api/seasons';
import { useToasts } from '../../hooks';
import ExportSeasonModal from './ExportSeasonModal';
import { useUrlModals } from '../../utils/useUrlModals';

const ReportsPageWrapper = ({ organizationId }) => {
  const { showToast, hideToast } = useToasts();
  // state to disable seasons button if no seasons for organization
  const [isSeasonsButtonDisabled, setIsSeasonsButtonDisabled] = useState(true);
  const [seasons, setSeasons] = useState([]);
  const [seasonToExport, setSeasonToExport] = useState([]);

  const { modals, openModal, closeModal } = useUrlModals({ exportSeason: {} });
  const seasonModal = modals.exportSeason || { isOpen: false, value: null };

  useEffect(() => {
    getSeasons(organizationId)
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      })
      .then((data) => {
        setIsSeasonsButtonDisabled(data.seasons.length === 0);
        if (data.seasons.length > 0) setSeasons(data.seasons);
      })
      .catch(() => setIsSeasonsButtonDisabled(true));
  }, [organizationId]);

  const exportToastDuration = 0;
  const toastId = useId();

  const handleExport = (type, season = undefined) => {
    const seasonName = season?.name ? season.name : undefined;
    const errorMessage = `There was an error exporting ${seasonName || type}. Please try again or contact us.`;
    const apiRequest = type === 'seasons' ? getSeasonExport(organizationId, season.id) : getExport(organizationId, type);

    apiRequest
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText || errorMessage);
        return response.json();
      })
      .then((data) => {
        if (!data.export_file) throw new Error(errorMessage);
        if (data.export_file.status === 'error') throw new Error(data.export_file.data || errorMessage);

        const { filename, id } = data.export_file;
        showToast({ message: 'Preparing export', type: 'info', isSpinner: true }, exportToastDuration, toastId);
        getFile({
          exportId: id, organizationId, filename, showToast, hideToast, toastId, type, seasonName, errorMessage,
        });
      })
      .catch((error) => showToast({ message: error?.message || errorMessage, type: 'error' }));
  };

  const handleExportButton = (type) => {
    return type === 'seasons'
      ? () => openModal('exportSeason', true)
      : () => handleExport(type);
  };

  const handleSeasonExport = () => {
    handleExport('seasons', seasonToExport);
    closeModal('exportSeason');
  };

  const handleCloseExportSeasonModal = () => closeModal('exportSeason');

  if (exportOptions.length === 0) return null;

  return (
    <>
      <div className="flex w-full bg-vamos-v-light-gray rounded-10px">
        <div className="mx-7 mt-5.25 mb-6.5 w-full space-y-10.5">
          <div className="space-y-2">
            <h4 className="text-2xl font-bold">Bulk Export</h4>
            <p className="text-lg">Choose a category to generate a CSV report.</p>
          </div>
          <div className="flex flex-col gap-y-4 md2:flex-row w-full md2:justify-between">
            {exportOptions.map((option) => (
              <Button
                label={option.label}
                size="md"
                color="secondary"
                className="max-w-34 xl:w-34 font-medium"
                onClick={handleExportButton(option.name)}
                key={option.label}
                isDisabled={option.name === 'seasons' && isSeasonsButtonDisabled}
              />
            ))}
          </div>
        </div>
      </div>
      {seasonModal.isOpen && (
        <ExportSeasonModal
          seasons={seasons}
          onCloseModal={handleCloseExportSeasonModal}
          onCheck={(season) => setSeasonToExport(season)}
          handleSeasonExport={handleSeasonExport}
        />
      )}
    </>
  );
};

ReportsPageWrapper.propTypes = { organizationId: PropTypes.number.isRequired };

export default ReportsPageWrapper;
